import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { useAuth } from "@/lib/auth";
import { fmtRelative } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, Stat, StatusPill } from "@/components/dash/ui";

function DeliveryStatusPanel() {
  const q = useQuery({ queryKey: ["briefing-delivery-status"], queryFn: dashApi.briefingDeliveryStatus });
  if (q.isLoading) return <LoadingState label="Loading delivery status…" />;
  if (q.isError) return <ErrorState error={q.error} />;
  const d = q.data;
  if (!d) return <EmptyState compact>No delivery status.</EmptyState>;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Scheduled delivery" value={d.schedule?.enabled ? "On" : "Off"}
          sub={d.schedule?.enabled && d.schedule.next_run_at ? `next ${fmtRelative(d.schedule.next_run_at)}` : "manual only"} />
        <Stat label="Last sent" value={d.last_sent_at ? fmtRelative(d.last_sent_at) : "—"} />
        <Stat label="Last failed" value={d.last_failed_at ? fmtRelative(d.last_failed_at) : "—"}
          sub={d.last_failed_detail ?? undefined} />
        <Stat label="Last generated" value={d.last_run ? fmtRelative(d.last_run.generated_at) : "—"}
          sub={d.last_run?.trigger} />
      </div>
      {d.deliveries.length > 0 && (
        <ul className="space-y-1 text-[11px] text-slate-500">
          {d.deliveries.slice(0, 6).map((x) => (
            <li key={x.id} className="flex items-center gap-2">
              <span className="text-slate-300">{x.status}</span>
              <span>· {x.channel_type}</span>
              <span>· {fmtRelative(x.last_attempt_at ?? x.created_at)}</span>
              {x.detail && <span className="truncate">· {x.detail}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function DailyBriefingPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const isAdmin = (auth.user?.roles ?? []).includes("admin");
  const q = useQuery({ queryKey: ["briefing-daily"], queryFn: dashApi.briefingDaily });
  const [ranId, setRanId] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: dashApi.briefingSend,
    onSuccess: (r) => {
      if (r.suppressed) { setMsg(r.reason ?? "Suppressed — nothing to brief."); setRanId(null); }
      else { setMsg(null); setRanId(r.report?.id ?? null); }
      qc.invalidateQueries({ queryKey: ["briefing-delivery-status"] });
    },
  });
  const d = q.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Daily Briefing"
        description="A deterministic morning briefing assembled from IRONDash's own services — every line traces back to real data. No AI-generated analysis; missing or stale data is stated honestly, and a send is safely suppressed when there is no data to brief."
        right={
          <div className="flex items-center gap-2">
            {ranId && <a href={dashApi.reportExportUrl(ranId, "html")} target="_blank" rel="noreferrer" className="rounded-lg border border-ink-500/60 bg-ink-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-ink-700">Open printable ↗</a>}
            {isAdmin && (
              <button type="button" onClick={() => send.mutate()} disabled={send.isPending}
                className="rounded-lg border border-brand-500/40 bg-brand-500/10 px-3 py-1.5 text-xs text-brand-200 hover:bg-brand-500/20 disabled:opacity-60">
                {send.isPending ? "Sending…" : send.isSuccess && !msg ? "Sent ✓" : "Generate & deliver"}
              </button>
            )}
          </div>
        }
      />
      {msg && <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-2 text-sm text-amber-200">{msg}</div>}

      <Panel title="Delivery status">
        <DeliveryStatusPanel />
      </Panel>

      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !d ? (
        <EmptyState>No briefing available.</EmptyState>
      ) : (
        <>
          <p className="text-xs text-slate-500">
            Since {d.since} · generated {d.generated_at.slice(0, 16).replace("T", " ")} UTC.
            {d.has_data === false && " No source data ingested yet — figures are honestly empty."}
          </p>
          <div className="grid gap-6 lg:grid-cols-2">
            {d.sections.map((s) => (
              <Panel key={s.key} title={s.title}>
                {!s.items.length ? <EmptyState compact>Nothing to report.</EmptyState> : (
                  <ul className="space-y-2">
                    {s.items.map((it, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        {it.severity !== "ok" && it.severity !== "info" ? <StatusPill status={it.severity} /> : <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-600" aria-hidden />}
                        {it.link ? (
                          <Link to={it.link} className="text-sm text-slate-300 hover:text-brand-300">{it.text}</Link>
                        ) : (
                          <span className="text-sm text-slate-300">{it.text}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
